import { BUILTIN_PACKS, REFERENCE_LOCALE } from './builtin.js';
import { compileMessage, type CompiledMessage, type MessageParams } from './message-format.js';
import { baseLocale, normalizeLocale, type FlatMessages } from './pack.js';
import type { Locale, MessageKey } from './types.js';

export const DEFAULT_LOCALE: Locale = REFERENCE_LOCALE;

export type Translate = (key: MessageKey, params?: MessageParams) => string;

/** Runtime messages per locale (packs from the server: overrides and extra languages). */
export type RuntimeCatalogues = Readonly<Record<Locale, FlatMessages>>;

/** Lookup chain for a locale: de-AT → de → en. */
function chain(locale: Locale): Locale[] {
  const base = baseLocale(locale);
  return [...new Set([locale, ...(base ? [base] : []), REFERENCE_LOCALE])];
}

/** Effective messages of one locale: built-in pack, then runtime pack on top. */
function lookup(locale: Locale, key: string, runtime: RuntimeCatalogues): string | undefined {
  return runtime[locale]?.[key] ?? BUILTIN_PACKS[locale]?.messages[key];
}

/**
 * Creates a translate function. Order per key: runtime(locale) → built-in(locale) → base
 * language → runtime(en) → built-in(en) → the key itself, so a gap never breaks the UI and an
 * English override (e.g. a company term) also reaches languages that lack the key.
 */
export function createTranslator(locale: Locale, runtime: RuntimeCatalogues = {}): Translate {
  const order = chain(locale);
  const compiled = new Map<string, CompiledMessage>();
  return (key, params) => {
    let message = compiled.get(key);
    if (!message) {
      let source: string | undefined;
      let sourceLocale = locale;
      for (const candidate of order) {
        source = lookup(candidate, key, runtime);
        if (source !== undefined) {
          sourceLocale = candidate;
          break;
        }
      }
      message = source === undefined ? () => key : compileMessage(source, sourceLocale);
      compiled.set(key, message);
    }
    return message(params);
  };
}

/**
 * Picks the best locale from BCP 47 tags (e.g. `navigator.languages`) among `available`:
 * exact match first, then the base language. Returns undefined when nothing matches.
 */
export function matchLocale(tags: readonly string[], available: readonly Locale[]): Locale | undefined {
  for (const tag of tags) {
    const normalized = normalizeLocale(tag);
    if (!normalized) continue;
    if (available.includes(normalized)) return normalized;
    const base = baseLocale(normalized);
    if (base && available.includes(base)) return base;
  }
  return undefined;
}

export interface ResolveLocaleInput {
  /** The user's own choice (stored preference). */
  preferred?: string | null;
  /** Browser languages. */
  browser: readonly string[];
  /** Enabled locales. */
  available: readonly Locale[];
  /** Instance default (admin setting). */
  instanceDefault?: Locale;
}

/** Language selection: user preference → browser → instance default → English. */
export function resolveLocale({ preferred, browser, available, instanceDefault }: ResolveLocaleInput): Locale {
  if (preferred && available.includes(preferred)) return preferred;
  return (
    matchLocale(browser, available) ??
    (instanceDefault && available.includes(instanceDefault) ? instanceDefault : undefined) ??
    (available.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : (available[0] ?? DEFAULT_LOCALE))
  );
}
